// ── Athena Image Engine ───────────────────────────────────────────────────────
// The UI talks only to the Athena Worker contract. It never selects an image
// host, builds an unauthenticated provider URL, or treats a preview as a saved
// resource. Provider choice, quota and cost policy live server-side.

import { API_BASE } from '../config/api';

// ── Explicit image request (Athena 2.0 trigger) ───────────────────────────────
const EXPLICIT_RE = /\b(ger[ae]|cri[ae]|fa[çz][ae]?|mostre?|desenhe?|produza?|mande?)\s+(uma?\s+)?(imagem|foto|ilustra[çc][aã]o|visual|figura|diagrama|mapa\s+mental|arte)\b/i;

export function isExplicitImageRequest(message) {
  return EXPLICIT_RE.test((message || '').trim());
}

// ── Auto visual aid (Foco Total + visual profile) ─────────────────────────────
const VISUAL_TOPICS_RE = /\b(sistema solar|planeta[s]?|fotoss[íi]ntese|c[eé]lula[s]?|dna|diagrama[s]?|mapa mental|linha do tempo|arquitetura|fluxograma|processo|salmo|vers[íi]culo|b[íi]blia|estudo b[íi]blico|biologia|qu[íi]mica|f[íi]sica|matem[aá]tica|hist[oó]ria|geografia|ciclo[s]?|estrutura|organograma|hierarquia|infogr[aá]fico|como funciona|o que [eé]|explica[çc][aã]o|fotoss[íi]ntese|evolu[çc][aã]o|anatomia|ec[oa]ssistema)\b/i;
const CASUAL_RE        = /^\s*((bom\s+dia|boa\s+(tarde|noite)|ol[aá]|oi|obrigad[ao]|valeu|tudo\s+(bem|bom)|sim|n[aã]o|ok|certo|legal|entend[ie]|perfeito|que\s+(bom|legal)|show)[!.?]?\s*)+$/i;

export function shouldGenerateVisualAid({ message, cognitiveProfile, mode }) {
  if (mode !== 'foco_total') return false;
  const vis = cognitiveProfile?.scores?.VISUAL || 0;
  if (vis < 6) return false;
  const text = (message || '').trim();
  if (text.length < 12) return false;
  if (CASUAL_RE.test(text)) return false;
  return VISUAL_TOPICS_RE.test(text);
}

// ── Safe visual prompt builder ───────────────────────────────────────────────
// This suggests an educational composition; the Worker is still the only
// authority that can submit the provider request.
export function buildVisualPrompt({ message, cognitiveProfile }) {
  const text = (message || '').trim();

  // Strip instruction prefixes → extract core topic
  const topic = text
    .replace(/^(explique?|fale\s+sobre|o\s+que\s+[eé]|como\s+funciona|quero\s+(estudar?|entender?)|me\s+explique?|estudar?)\s+/i, '')
    .replace(/^(o\s+)?salmo\s+/i, 'Psalm ')
    .slice(0, 120)
    .trim();

  const isVisualPrimary = cognitiveProfile?.traits?.[0] === 'VISUAL';
  const baseStyle = isVisualPrimary
    ? 'modern flat design educational illustration'
    : 'clean minimal educational diagram';

  return `${baseStyle}, ${topic}, no text overlays, no watermarks, clean composition, educational reference style, blue indigo tones, soft light, high quality`;
}

export class AthenaImageGenerationError extends Error {
  constructor({ code = 'IMAGE_GENERATION_FAILED', message, retryAfterSeconds = null, result = null } = {}) {
    super(message || 'Não foi possível gerar a imagem.');
    this.name = 'AthenaImageGenerationError';
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.result = result;
  }
}

function safePrompt(value) {
  return typeof value === 'string'
    ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 1000)
    : '';
}

async function readImageResponse(response) {
  try { return await response.json(); } catch { return null; }
}

// ── Backend call ──────────────────────────────────────────────────────────────
// Returns the canonical action result from the Worker. A successful result has
// an image object and a provider URL; it does not imply the file was persisted
// unless image.persistent is explicitly true.
export async function generateImage({ prompt, mode = 'athena2', projectId = null, sessionId = null, actionId = null, idempotencyKey = null, width = 1024, height = 768, apiBase = API_BASE }, token) {
  const cleanPrompt = safePrompt(prompt);
  if (!cleanPrompt) {
    throw new AthenaImageGenerationError({
      code: 'IMAGE_PROMPT_REQUIRED',
      message: 'Descreva a imagem que você quer gerar.',
    });
  }
  const res = await fetch(`${apiBase}/generate-image`, {
    method:      'POST',
    headers:     { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    credentials: 'include',
    body: JSON.stringify({
      prompt: cleanPrompt,
      mode,
      project_id: projectId || undefined,
      session_id: sessionId || undefined,
      action_id: actionId || undefined,
      idempotency_key: idempotencyKey || actionId || undefined,
      width,
      height,
    }),
  });
  const payload = await readImageResponse(res);
  if (!res.ok || payload?.success !== true) {
    throw new AthenaImageGenerationError({
      code: payload?.errorCode || payload?.error_code || `IMAGE_REQUEST_${res.status}`,
      message: payload?.userMessage || payload?.detail || 'Não foi possível confirmar a geração da imagem.',
      retryAfterSeconds: payload?.retry_after_seconds || null,
      result: payload || null,
    });
  }
  return payload;
}

export async function getAthenaImageCapabilities(token) {
  const res = await fetch(`${API_BASE}/images/capabilities`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  const payload = await readImageResponse(res);
  if (!res.ok) throw new AthenaImageGenerationError({
    code: payload?.error_code || `IMAGE_CAPABILITIES_${res.status}`,
    message: payload?.detail || 'Não consegui verificar a disponibilidade de imagens.',
  });
  return payload;
}
